import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  FiUserCheck, FiPlus, FiX, FiSave, FiRotateCcw, FiAlertTriangle, FiSlash
} from 'react-icons/fi';
import { Button, confirmAlert } from 'components/common';
import { INPUT_CLS } from 'helpers/formStyles';
import { escapeHtml } from 'helpers/utils';
import { updateImpersonation } from 'redux/integration/applicationSlice';
import ImpersonationBadge, {
  ANY_USER, ALLOWLIST, IMPERSONATION_MODES, MAX_ALLOWLIST_SIZE, normalizeEmails, sameEmails
} from './impersonation';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SEPARATORS = /[,;\s]+/;

// ── One selectable mode card ──────────────────────────────────────────────────
const ModeCard = ({ mode, selected, onSelect }) => {
  const Icon = mode.icon;
  const isOpen = mode.value === ANY_USER;
  const accent = selected
    ? (isOpen ? 'tw-border-amber-400 tw-bg-amber-50/60' : 'tw-border-emerald-400 tw-bg-emerald-50/60')
    : 'tw-border-slate-200 tw-bg-white hover:tw-border-slate-300 hover:tw-bg-slate-50';

  return (
    <button
      type="button"
      onClick={() => onSelect(mode.value)}
      aria-pressed={selected}
      className={`tw-text-left tw-rounded-lg tw-border tw-p-4 tw-transition-colors tw-cursor-pointer tw-outline-none ${accent}`}
    >
      <div className="tw-flex tw-items-start tw-gap-3">
        <span
          className={`tw-mt-0.5 tw-w-4 tw-h-4 tw-rounded-full tw-border-2 tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center ${
            selected ? (isOpen ? 'tw-border-amber-500' : 'tw-border-emerald-500') : 'tw-border-slate-300'
          }`}
        >
          {selected && (
            <span className={`tw-w-2 tw-h-2 tw-rounded-full ${isOpen ? 'tw-bg-amber-500' : 'tw-bg-emerald-500'}`} />
          )}
        </span>
        <div className="tw-min-w-0">
          <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1">
            <Icon size={14} className={isOpen ? 'tw-text-amber-600' : 'tw-text-emerald-600'} />
            <p className="tw-text-sm tw-font-semibold tw-text-slate-800 tw-mb-0">{mode.title}</p>
            <span className="tw-text-[11px] tw-text-slate-400">· {mode.caption}</span>
          </div>
          <p className="tw-text-xs tw-text-slate-500 tw-leading-relaxed tw-mb-0">{mode.description}</p>
        </div>
      </div>
    </button>
  );
};

ModeCard.propTypes = {
  mode: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};

// ── Panel ─────────────────────────────────────────────────────────────────────
// The screen equivalent of `rake oauth:restrict / oauth:allow / oauth:disallow /
// oauth:open`: one place to answer "whose token may this client ask for?" for a
// client that was just registered, without an SSH session.
//
// Nothing is written until Save: the mode and the list are edited together and
// sent as one decision, so a client is never briefly restricted-with-an-empty-
// allowlist (which refuses every request) on the way to being configured.
const ImpersonationPanel = ({ record }) => {
  const dispatch = useDispatch();
  const saving = useSelector((s) => s.integration_application.savingImpersonation);

  const savedMode   = record?.impersonation_mode || ALLOWLIST;
  const savedEmails = useMemo(() => normalizeEmails(record?.impersonatable_emails), [record?.impersonatable_emails]);

  const [mode, setMode]     = useState(savedMode);
  const [emails, setEmails] = useState(savedEmails);
  const [draft, setDraft]   = useState('');

  // Re-seed whenever the record changes (first load, save, navigating between
  // applications) — the panel is a view of the server's answer, not its own.
  useEffect(() => {
    setMode(savedMode);
    setEmails(savedEmails);
    setDraft('');
  }, [record?.id, savedMode, savedEmails]);

  const dirty = mode !== savedMode || !sameEmails(emails, savedEmails);
  const restricted = mode === ALLOWLIST;

  // Splits on commas, semicolons and whitespace, so a list pasted out of an
  // email or a ticket is added in one go instead of failing the pattern as one
  // long "address".
  const addEmail = () => {
    const candidates = draft.toLowerCase().split(SEPARATORS).map((e) => e.trim()).filter(Boolean);
    if (candidates.length === 0) return;

    const invalid = candidates.filter((email) => !EMAIL_PATTERN.test(email));
    if (invalid.length > 0) {
      toast.error(`Not an email address: ${invalid.join(', ')}`);
      return;
    }

    const added = candidates.filter((email) => !emails.includes(email));
    if (added.length === 0) {
      toast.info('Already on the allowlist.');
      setDraft('');
      return;
    }

    if (emails.length + added.length > MAX_ALLOWLIST_SIZE) {
      toast.error(`An allowlist may hold at most ${MAX_ALLOWLIST_SIZE} addresses.`);
      return;
    }

    setEmails(normalizeEmails([...emails, ...added]));
    setDraft('');
  };

  const removeEmail = (email) => setEmails(emails.filter((e) => e !== email));

  const handleKeyDown = (event) => {
    // Enter inside a panel that sits in no form of its own would otherwise do
    // nothing; comma and space are how people paste a list.
    if (['Enter', ',', ' '].includes(event.key)) {
      event.preventDefault();
      addEmail();
    }
  };

  const reset = () => {
    setMode(savedMode);
    setEmails(savedEmails);
    setDraft('');
  };

  const save = () => {
    const payload = {
      applicationId: record?.id,
      impersonationMode: mode,
      impersonatableEmails: emails,
    };

    // Widening is the change worth a second look: from here one leaked client
    // secret mints a token for anybody, admins included.
    if (mode === ANY_USER && savedMode !== ANY_USER) {
      confirmAlert({
        title: 'Allow impersonation of any user?',
        message:
          `<strong>${escapeHtml(record?.name || 'This client')}</strong> will be able to mint a portal token for ` +
          'any user, with its client secret as the only check. Use this only for an SSO integration that serves the whole staff directory.',
        type: 'warning',
        buttons: [
          { label: 'Allow any user', variant: 'danger', onClick: () => dispatch(updateImpersonation(payload)) },
          { label: 'Cancel', variant: 'secondary', onClick: () => {} },
        ],
      });
      return;
    }

    dispatch(updateImpersonation(payload));
  };

  return (
    <div className="tw-rounded-lg tw-border tw-border-slate-200 tw-overflow-hidden">
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-5 tw-py-3 tw-bg-slate-50 tw-border-b tw-border-slate-200">
        <div className="tw-flex tw-items-center tw-gap-2">
          <FiUserCheck size={13} className="tw-text-slate-500" />
          <p className="tw-text-sm tw-font-semibold tw-text-slate-700 tw-mb-0">Token impersonation</p>
        </div>
        <ImpersonationBadge record={record} />
      </div>

      <div className="tw-px-5 tw-py-4 tw-bg-white tw-space-y-4">
        <p className="tw-text-xs tw-text-slate-500 tw-mb-0">
          Whose token this client may ask for through the <code className="tw-text-slate-700">email_credentials</code> grant.
          A token minted here is a full portal identity, so keep it as narrow as the integration actually needs.
        </p>

        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-3">
          {IMPERSONATION_MODES.map((m) => (
            <ModeCard key={m.value} mode={m} selected={mode === m.value} onSelect={setMode} />
          ))}
        </div>

        {/* ── Allowlist editor ── */}
        <div className={restricted ? '' : 'tw-opacity-60'}>
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mb-2">
            <p className="tw-text-[11px] tw-font-semibold tw-text-slate-400 tw-uppercase tw-tracking-wider tw-mb-0">
              Allowed addresses {emails.length > 0 && `(${emails.length})`}
            </p>
            {!restricted && emails.length > 0 && (
              <span className="tw-text-[11px] tw-text-slate-400">Kept, but not enforced while in “Any user”</span>
            )}
          </div>

          <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={!restricted}
              placeholder="service-account@pebsteel.com.vn"
              className={INPUT_CLS}
              aria-label="Address to allow"
            />
            <Button size="sm" variant="secondary" onClick={addEmail} disabled={!restricted || !draft.trim()}>
              <FiPlus size={13} /> Add
            </Button>
          </div>

          {emails.length === 0 ? (
            <div className="tw-rounded-lg tw-border tw-border-dashed tw-border-slate-200 tw-px-4 tw-py-5 tw-text-center">
              <FiSlash size={16} className="tw-text-slate-300 tw-mb-1" />
              <p className="tw-text-xs tw-text-slate-500 tw-mb-0">
                {restricted
                  ? 'No address allowed — this client cannot mint a token for anybody.'
                  : 'No address listed. Switch to “Restricted allowlist” to bound this client.'}
              </p>
            </div>
          ) : (
            <div className="tw-flex tw-flex-wrap tw-gap-2">
              {emails.map((email) => (
                <span
                  key={email}
                  className="tw-inline-flex tw-items-center tw-gap-1.5 tw-pl-3 tw-pr-1.5 tw-py-1 tw-rounded-xl tw-bg-slate-100 tw-text-slate-700 tw-text-xs tw-font-medium"
                >
                  {email}
                  <button
                    type="button"
                    onClick={() => removeEmail(email)}
                    disabled={!restricted}
                    title={`Remove ${email}`}
                    aria-label={`Remove ${email}`}
                    className="tw-p-0.5 tw-rounded-full tw-text-slate-400 hover:tw-text-red-600 hover:tw-bg-white tw-transition-colors tw-border-0 tw-bg-transparent tw-cursor-pointer disabled:tw-cursor-not-allowed"
                  >
                    <FiX size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {restricted && emails.length === 0 && savedEmails.length > 0 && (
          <div className="tw-flex tw-items-start tw-gap-2 tw-rounded tw-bg-amber-50 tw-border tw-border-amber-200 tw-px-3 tw-py-2">
            <FiAlertTriangle size={13} className="tw-text-amber-500 tw-mt-0.5 tw-flex-shrink-0" />
            <p className="tw-text-xs tw-text-amber-800 tw-mb-0">
              Saving an empty allowlist switches this integration off — every request it makes to the token endpoint will be refused.
            </p>
          </div>
        )}

        <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-pt-1 tw-border-t tw-border-slate-100">
          <span className="tw-text-xs tw-text-slate-400 tw-mr-auto tw-pt-3">
            {dirty ? 'Unsaved changes' : 'Every address must belong to an active portal user.'}
          </span>
          <Button size="sm" variant="ghost" onClick={reset} disabled={!dirty || saving} className="tw-mt-3">
            <FiRotateCcw size={13} /> Discard
          </Button>
          <Button size="sm" variant="primary" onClick={save} disabled={!dirty} loading={saving} className="tw-mt-3">
            <FiSave size={13} /> Save policy
          </Button>
        </div>
      </div>
    </div>
  );
};

ImpersonationPanel.propTypes = {
  record: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    impersonation_mode: PropTypes.string,
    impersonatable_emails: PropTypes.arrayOf(PropTypes.string),
  }),
};

export default ImpersonationPanel;
